import IconBold from '@apollo/icons/default/IconBold.svg';
import IconCodeBlock from '@apollo/icons/default/IconCodeBlock.svg';
import IconItalic from '@apollo/icons/default/IconItalic.svg';
import IconLink from '@apollo/icons/default/IconLink.svg';
import IconListBullet from '@apollo/icons/default/IconListBullet.svg';
import IconListNumber from '@apollo/icons/default/IconListNumber.svg';
import IconMarkdownSolid from '@apollo/icons/default/IconMarkdownSolid.svg';
import IconMention from '@apollo/icons/default/IconMention.svg';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import IconQuote from '@apollo/icons/default/IconQuote.svg';
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useMarkdownTransforms } from 'src/hooks/useMarkdownTransforms';
import {
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import { assertUnreachable } from 'src/lib/assertUnreachable';

import { useDerivedEditorValue } from '../../explorerPage/hooks/useDerivedEditorValue';

import { SafeCommentMarkdown } from './SafeCommentMarkdown';
import { useGetUsersForMentions, useMentionUsers } from './useMentionUsers';

const ICON_WIDTH = 28;
const ICON_SPACE = 8;

export function CommentMarkdownEditor({
  className,
  defaultValue,
  shouldShowPlaceholderText = true,
  shouldAutoFocusEditor,
  actions,
  usersForMentions: usersForMentioning,
}: {
  className?: string;
  defaultValue?: string;
  shouldShowPlaceholderText?: boolean;
  shouldAutoFocusEditor?: boolean;
  actions: (props: {
    hasComment: boolean;
    hasChanged: boolean;
    getValue: () => string;
    setValue: (value: string) => void;
  }) => React.ReactNode | React.ReactNode;
  usersForMentions: ReturnType<typeof useGetUsersForMentions>;
}) {
  const toolbarSize = useResizeDetector({ handleHeight: false });
  const [tab, setTab] = useState<'write' | 'preview'>('write');
  const editor = useStudioEditor({
    language: 'markdown',
    defaultValue: defaultValue ?? '',
    glyphMargin: false,
    lineNumbers: 'off',
    lineDecorationsWidth: 0,
  });

  useMentionUsers(usersForMentioning);
  const {
    makeSelectionBold,
    makeSelectionItalic,
    makeSelectionLink,
    makeSelectionQuote,
    makeSelectionCode,
  } = useMarkdownTransforms({ editor });

  const hasComment = useDerivedEditorValue({
    editor,
    getValue: useCallback((value: string) => value.trim().length > 0, []),
    withPosition: false,
  });

  const hasText = useDerivedEditorValue({
    editor,
    getValue: useCallback((value: string) => value.length > 0, []),
    withPosition: false,
  });

  const hasChanged = useDerivedEditorValue({
    editor,
    getValue: useCallback(
      (value: string) => value !== defaultValue,
      [defaultValue],
    ),
    withPosition: false,
  });

  useEffect(() => {
    if (shouldAutoFocusEditor) {
      editor.focus();
    }
  }, [editor, shouldAutoFocusEditor]);

  const editorActions =
    tab === 'write'
      ? [
          {
            key: 'bold',
            label: 'Bold',
            icon: IconBold,
            onClick: makeSelectionBold,
          },
          {
            key: 'italic',
            label: 'Italic',
            icon: IconItalic,
            onClick: makeSelectionItalic,
          },
          {
            key: 'quote',
            label: 'Quote',
            icon: IconQuote,
            onClick: makeSelectionQuote,
          },
          {
            key: 'code',
            label: 'Code',
            icon: IconCodeBlock,
            onClick: makeSelectionCode,
          },
          {
            key: 'mention',
            label: 'Mention user',
            icon: IconMention,
            onClick: _.noop,
            notImplemented: true,
          },
          {
            key: 'link',
            label: 'Insert link',
            icon: IconLink,
            onClick: makeSelectionLink,
          },
          {
            key: 'unordered list',
            label: 'Add bulleted list',
            icon: IconListBullet,
            onClick: _.noop,
            notImplemented: true,
          },
          {
            key: 'ordered list',
            label: 'Add numbered list',
            icon: IconListNumber,
            onClick: _.noop,
            notImplemented: true,
          },
        ].filter((a) => !a.notImplemented)
      : [];

  const availableActionSlots = toolbarSize.width
    ? Math.floor(toolbarSize.width / (ICON_WIDTH + ICON_SPACE))
    : editorActions.length;
  const visibleActions =
    availableActionSlots < editorActions.length
      ? availableActionSlots - 1
      : availableActionSlots;

  const editorValue = editor.getValue();

  return (
    <Card
      isDivided
      className={classnames('overflow-hidden', className)}
      data-testid="comment_markdown_editor"
    >
      <CardHeader className="flex-row gap-4 px-4 pb-0 pt-4">
        <HeaderTab
          className="focus-visible:focus-ring !mx-0 text-sm"
          tabID="Write"
          isActive={() => tab === 'write'}
          onClick={() => setTab('write')}
        />
        <HeaderTab
          className="focus-visible:focus-ring !mx-0 text-sm"
          tabID="Preview"
          isActive={() => tab === 'preview'}
          onClick={() => setTab('preview')}
        />
      </CardHeader>
      <CardBody
        className={classnames('pb-0 pt-2', { 'px-0': tab === 'write' })}
      >
        {tab === 'preview' ? (
          <SafeCommentMarkdown
            comment={hasComment ? editorValue : 'Nothing to preview'}
            usersForMentions={usersForMentioning}
            className="h-24 overflow-auto"
          />
        ) : tab === 'write' ? (
          <div className="relative h-24">
            <StudioEditorContainer editor={editor} className="h-full" />
            {!hasText && shouldShowPlaceholderText && (
              <div
                // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                // TODO(Maya) add link to synthetics test
                data-testid="proposals_add_comment_text_field"
                className="pointer-events-none absolute top-1.5 pl-4 font-mono text-[13px] text-placeholder"
              >
                Leave a comment
              </div>
            )}

            <IconMarkdownSolid className="pointer-events-none absolute bottom-2 right-6" />
          </div>
        ) : (
          assertUnreachable(tab)
        )}
      </CardBody>
      <CardFooter className="px-4 py-3">
        <div ref={toolbarSize.ref} className="mr-2 flex-1 space-x-2">
          {editorActions
            .slice(0, visibleActions)
            .map(({ label, key, ...props }) => (
              <Tooltip label={label} key={key}>
                <IconButton
                  variant="hidden"
                  size="sm"
                  {...props}
                  aria-label={label}
                  icon={<props.icon />}
                />
              </Tooltip>
            ))}
          {visibleActions < editorActions.length ? (
            <Menu placement="bottom-end" isLazy autoSelect={false}>
              <MenuButton as={IconButton} variant="hidden" size="sm">
                <IconOverflow />
              </MenuButton>
              <MenuContent>
                <MenuList>
                  {editorActions
                    .slice(visibleActions)
                    .map(({ label, ...props }) => {
                      return (
                        <MenuItem
                          {...props}
                          icon={<props.icon />}
                          key={props.key}
                        >
                          {label}
                        </MenuItem>
                      );
                    })}
                </MenuList>
              </MenuContent>
            </Menu>
          ) : null}
        </div>
        {typeof actions === 'function'
          ? actions({
              hasComment,
              hasChanged,
              getValue: () => editor.getValue(),
              setValue: (value: string) => editor.getModel()?.setValue(value),
            })
          : actions}
      </CardFooter>
    </Card>
  );
}
